import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
  SelectPriority,
  SelectHorizon,
} from '@/components/ui/select'
import type { User, Workspace, Project, Task, Document } from '@/types/nodes'
import type { BaseFilterSectionProps } from '@/types/filters'
import { Priority, Horizon } from '@/types/enums'

/**
 * BaseFilterSection Component
 *
 * A reusable component for rendering individual filter controls.
 * Handles different types of filter options including user/workspace selections.
 */
export function BaseFilterSection<T extends Record<string, string | number | symbol>>({
  config,
  value,
  onChange,
  availableUsers = [],
  availableWorkspaces = [],
  availableProjects = [],
  availableTasks = [],
  availableDocuments = [],
}: BaseFilterSectionProps<T>) {
  const renderOptions = () => {
    // Handle special cases for user/workspace selections
    if (config.key === 'assignee' || config.key === 'creator' || config.key === 'user') {
      return (
        <>
          <SelectItem value="all">
            All{' '}
            {config.key === 'assignee'
              ? 'Assignees'
              : config.key === 'creator'
                ? 'Creators'
                : 'Team Members'}
          </SelectItem>
          {availableUsers.map((user: User) => (
            <SelectItem key={user.id} value={user.id}>
              {user.name}
            </SelectItem>
          ))}
        </>
      )
    }

    if (config.key === 'workspace') {
      return (
        <>
          <SelectItem value="all">All Workspaces</SelectItem>
          {availableWorkspaces.map((workspace: Workspace) => (
            <SelectItem key={workspace.id} value={workspace.id}>
              {workspace.name}
            </SelectItem>
          ))}
        </>
      )
    }

    if (config.key === 'project') {
      return (
        <>
          <SelectItem value="all">All Projects</SelectItem>
          {availableProjects.map((project: Project) => (
            <SelectItem key={project.id} value={project.id}>
              {project.name}
            </SelectItem>
          ))}
        </>
      )
    }

    if (config.key === 'task') {
      return (
        <>
          <SelectItem value="all">All Tasks</SelectItem>
          {availableTasks.map((task: Task) => (
            <SelectItem key={task.id} value={task.id}>
              {task.name}
            </SelectItem>
          ))}
        </>
      )
    }

    if (config.key === 'document') {
      return (
        <>
          <SelectItem value="all">All Documents</SelectItem>
          {availableDocuments.map((doc: Document) => (
            <SelectItem key={doc.id} value={doc.id}>
              {doc.name}
            </SelectItem>
          ))}
        </>
      )
    }

    // Default option rendering
    return config.options.map((option: { value: string; label: string }) => (
      <SelectItem key={option.value} value={option.value}>
        {option.label}
      </SelectItem>
    ))
  }

  // Use specialized components for Priority and Horizon filters
  if (config.key === 'priority') {
    return (
      <div className={config.gridColumn}>
        <label className="text-sm font-medium mb-2 block">{config.label}</label>
        <SelectPriority
          value={value as Priority | 'all'}
          onValueChange={onChange}
          placeholder={`All ${config.label.toLowerCase()}`}
          includeAllOption={true}
        />
      </div>
    )
  }

  if (config.key === 'horizon') {
    return (
      <div className={config.gridColumn}>
        <label className="text-sm font-medium mb-2 block">{config.label}</label>
        <SelectHorizon
          value={value as Horizon | 'all'}
          onValueChange={onChange}
          placeholder={`All ${config.label.toLowerCase()}`}
          includeAllOption={true}
        />
      </div>
    )
  }

  return (
    <div className={config.gridColumn}>
      <label className="text-sm font-medium mb-2 block">{config.label}</label>
      <Select value={String(value)} onValueChange={onChange}>
        <SelectTrigger>
          <SelectValue placeholder={`All ${config.label.toLowerCase()}`} />
        </SelectTrigger>
        <SelectContent>{renderOptions()}</SelectContent>
      </Select>
    </div>
  )
}
